import { z } from 'zod';
import { cashVoucherRowSchema, debtMovementRowSchema, messageRowSchema, participantSchema, purchaseRowSchema, revolvingBalanceRowSchema, saleRowSchema, } from './dashboard.contract.js';
import { postingInfoSchema } from './inventory.contract.js';
import { receiptSchema } from './receipt.contract.js';
/**
 * GET /{family}/:id — the details dialog (fuel.md section 5.2): every field,
 * the participants, and the receipt for reprinting (null for opening balances).
 */
const detailsExtra = {
    participants: z.array(participantSchema),
    receipt: receiptSchema.nullable(),
};
export const revolvingBalanceDetailsSchema = revolvingBalanceRowSchema.extend(detailsExtra);
export const purchaseDetailsSchema = purchaseRowSchema.extend({
    ...detailsExtra,
    /** The PDA purchase's WhatsApp messages, newest first (empty for an operator purchase). */
    messages: z.array(messageRowSchema),
    posting: postingInfoSchema,
});
export const saleDetailsSchema = saleRowSchema.extend({
    ...detailsExtra,
    posting: postingInfoSchema,
});
export const debtMovementDetailsSchema = debtMovementRowSchema.extend({
    ...detailsExtra,
    customersDeliveryFailed: z.boolean().optional(),
    messages: z.array(messageRowSchema),
    posting: postingInfoSchema,
});
/** Payments and receipts follow وضع الجرد too (KAN-42), so they carry the posting part. */
export const paymentDetailsSchema = cashVoucherRowSchema.extend({
    ...detailsExtra,
    posting: postingInfoSchema,
});
export const receiptDetailsSchema = cashVoucherRowSchema.extend({
    ...detailsExtra,
    posting: postingInfoSchema,
});
// --- look-ups for dashboard filters and forms --------------------------------
export const lookupQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    limit: z.coerce.number().int().positive().max(50).default(20),
});
/** The bulk-message checklist lists every debtor, so this look-up allows up to 1000. */
export const debtorLookupQuerySchema = z.object({
    search: z.string().trim().max(100).optional(),
    limit: z.coerce.number().int().positive().max(1000).default(20),
    branchId: z.string().optional(),
});
export const vehicleLookupQuerySchema = lookupQuerySchema.extend({ debtorId: z.string().min(1) });
export const debtorOptionSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    name: z.string(),
    phone: z.string().nullable(),
    balance: z.string(),
    debtLimit: z.string(),
    branchId: z.string().nullable(),
    /** Only LINKED or PENDING debtors have a valid phone for messages. */
    linkStatus: z.enum(['LINKED', 'PENDING', 'INVALID_PHONE']),
});
export const vehicleOptionSchema = z.object({ id: z.string(), plate: z.string() });
export const employeeOptionSchema = z.object({ id: z.string(), name: z.string() });
/**
 * The data of the حركات الذمم PDF export (GET /debt-movements/pdf): every row
 * of the current filter (up to 5000) with its totals. Internal to the API,
 * which renders the PDF itself; never sent over the wire.
 */
export const debtMovementPrintSchema = z.object({
    rows: z.array(debtMovementRowSchema),
    totals: z.object({ charges: z.string(), repayments: z.string(), balance: z.string() }),
    truncated: z.boolean(),
    generatedAt: z.iso.datetime({ offset: true }),
});
/** Delivery confirmation (fuel.md section 4.2): no body, returns the new state. */
export const deliveryResultSchema = z.object({
    id: z.string(),
    deliveryStatus: z.literal('DELIVERED'),
    deliveredAt: z.iso.datetime({ offset: true }),
});
/** POST /messages/bulk result. */
export const bulkMessageResultSchema = z.object({
    queued: z.number().int(),
    skippedInvalidPhone: z.number().int(),
});
/** GET /system/state (both apps): the freeze flag and whether this is the archive. */
export const systemStateSchema = z.object({ frozen: z.boolean(), archive: z.boolean() });
/** Password re-check for opening year management (fuel.md section 5.11). */
export const yearPasswordSchema = z.object({ password: z.string().min(1).max(200) });
/**
 * GET /debt-movements/manual-lookup (W5): the categories and sale prices the
 * manual debt-movement drawer needs, for anyone who may create or edit debt
 * movements (without SETTINGS_VIEW or CATEGORY_LIST).
 */
export const manualDebtLookupSchema = z.object({
    categories: z.array(z.object({
        id: z.string(),
        type: z.enum(['BARREL', 'JERRYCAN']),
        name: z.string(),
        fraction: z.string(),
    })),
    prices: z.object({
        DIESEL: z.object({ barrel: z.string(), jerrycan: z.string() }),
        PETROL: z.object({ barrel: z.string(), jerrycan: z.string() }),
    }),
});
/** GET /purchases/price-defaults (PURCHASE_PRICE_COMPLETE): the saved purchase prices that prefill إكمال السعر. */
export const purchasePriceDefaultsSchema = z.object({
    DIESEL: z.object({ barrel: z.string(), jerrycan: z.string() }),
    PETROL: z.object({ barrel: z.string(), jerrycan: z.string() }),
});
//# sourceMappingURL=details.contract.js.map